import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import type { ReactElement } from "react";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Layout } from "./components/Layout";
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ConfirmAccountPage from "./pages/ConfirmAccountPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import PortfolioPage from "./pages/PortfolioPage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import TransactionHistoryPage from "./pages/TransactionHistoryPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AdminRolesPage from "./pages/AdminRolesPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminProjectsPage from "./pages/AdminProjectsPage";
import AdminInvestmentPacksPage from "./pages/AdminInvestmentPacksPage";
import AdminSimulatedTradesPage from "./pages/AdminSimulatedTradesPage";
import AdminCryptoWalletsPage from "./pages/AdminCryptoWalletsPage";
import ProfilePage from "./pages/ProfilePage";
import TradingPage from "./pages/TradingPage";
import TradingBotPage from "./pages/TradingBotPage";
import MessagesPage from "./pages/MessagesPage";
import AccountsPage from "./pages/AccountsPage";
import NotificationsPage from "./pages/NotificationsPage";
import InvestmentPacksPage from "./pages/InvestmentPacksPage";
import InvestmentsPage from "./pages/InvestmentsPage";
import SupportPage from "./pages/SupportPage";
import { useAuth } from "./hooks/useAuth";
import type { UserRole } from "./types/api";
import "./App.css";
import "./index.css";
import { Toaster } from "./components/ui/toaster";
import AdminLayout from "./components/AdminLayout";
import AdminMaintenancePage from "./pages/AdminMaintenancePage";
import SuperadminClientInvestmentsPage from "./pages/SuperadminClientInvestmentsPage";
import { GoogleOAuthProvider } from "@react-oauth/google";

const PrivateRoute = ({
  element,
  roles,
  permissions,
}: {
  element: ReactElement;
  roles?: UserRole[];
  permissions?: string[];
}) => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  const requiresCheck = Boolean(roles?.length || permissions?.length);
  if (requiresCheck) {
    const roleOk = roles ? Boolean(user && roles.includes(user.role)) : false;
    const permissionOk = permissions
      ? Boolean(
          user &&
            permissions.some((permission) =>
              user.permissions?.includes(permission)
            )
        )
      : false;

    if (!roleOk && !permissionOk) {
      return <Navigate to="/" replace />;
    }
  }
  return element;
};

const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<HomePage />} />
    <Route path="/login" element={<LoginPage />} />
    <Route path="/register" element={<RegisterPage />} />
    <Route path="/auth/confirm" element={<ConfirmAccountPage />} />
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    <Route path="/reset-password" element={<ResetPasswordPage />} />
    <Route path="/projects" element={<ProjectsPage />} />
    <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
    <Route path="/investment-packs" element={<InvestmentPacksPage />} />
    <Route
      path="/dashboard"
      element={<PrivateRoute element={<DashboardPage />} />}
    />
    <Route
      path="/portfolio"
      element={<PrivateRoute element={<PortfolioPage />} />}
    />
    <Route
      path="/transactions"
      element={<PrivateRoute element={<TransactionHistoryPage />} />}
    />
    <Route
      path="/profile"
      element={<PrivateRoute element={<ProfilePage />} />}
    />
    <Route
      path="/accounts"
      element={<PrivateRoute element={<AccountsPage />} />}
    />
    <Route
      path="/investments"
      element={<PrivateRoute element={<InvestmentsPage />} />}
    />
    <Route
      path="/notifications"
      element={<PrivateRoute element={<NotificationsPage />} />}
    />
    <Route path="/support" element={<SupportPage />} />
    <Route path="/trading" element={<TradingPage />} />
    <Route
      path="/trading-bot"
      element={<PrivateRoute element={<TradingBotPage />} />}
    />
    <Route
      path="/messages"
      element={
        <PrivateRoute
          element={<MessagesPage />}
          roles={[
            "client",
            "manager_contact",
            "manager_trader",
            "admin",
            "superadmin",
          ]}
        />
      }
    />
    <Route
      path="/admin/*"
      element={
        <PrivateRoute
          element={<AdminLayout />}
          roles={["admin", "manager_contact", "manager_trader", "superadmin"]}
          permissions={["trades.simulate"]}
        />
      }>
      <Route
        index
        element={
          <PrivateRoute element={<AdminDashboardPage />} roles={["admin"]} />
        }
      />
      <Route
        path="users"
        element={
          <PrivateRoute
            element={<AdminUsersPage />}
            roles={["admin", "manager_contact", "manager_trader"]}
          />
        }
      />
      <Route
        path="projects"
        element={
          <PrivateRoute element={<AdminProjectsPage />} roles={["admin"]} />
        }
      />
      <Route
        path="investment-packs"
        element={
          <PrivateRoute
            element={<AdminInvestmentPacksPage />}
            roles={["admin"]}
          />
        }
      />
      <Route
        path="roles"
        element={
          <PrivateRoute element={<AdminRolesPage />} roles={["admin"]} />
        }
      />
      <Route
        path="simulated-trades"
        element={
          <PrivateRoute
            element={<AdminSimulatedTradesPage />}
            roles={["admin", "manager_contact", "manager_trader"]}
            permissions={["trades.simulate"]}
          />
        }
      />
      <Route
        path="maintenance"
        element={
          <PrivateRoute
            element={<AdminMaintenancePage />}
            roles={["admin", "manager_contact", "manager_trader"]}
          />
        }
      />
      <Route
        path="crypto-wallets"
        element={
          <PrivateRoute
            element={<AdminCryptoWalletsPage />}
            roles={["admin"]}
          />
        }
      />
      <Route
        path="superadmin/client-investments"
        element={
          <PrivateRoute
            element={<SuperadminClientInvestmentsPage />}
            roles={["superadmin"]}
          />
        }
      />
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const CoreApp = () => (
  <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        <Layout>
          <AppRoutes />
        </Layout>
        <Toaster />
      </BrowserRouter>
    </AuthProvider>
  </ThemeProvider>
);

const App = () =>
  googleClientId ? (
    <GoogleOAuthProvider clientId={googleClientId}>
      <CoreApp />
    </GoogleOAuthProvider>
  ) : (
    <CoreApp />
  );

export default App;
